/**
 * SLC Configurator — Web Component
 *
 * Flows driven by Step 1 selection:
 *   "new"             → Penguins → Access Mode → Light Licenses (tier-based)
 *   "new-facility"    → Penguins → Access Mode → Light Licenses (call for pricing)
 *   "returning" + 2.licenses  → Light Licenses (call for pricing)
 *   "returning" + 2.penguins  → Penguins
 *
 * Light licenses appear in new/new-facility only when access mode is lan or cloud.
 * Cart adds Commander + Penguins (×qty) + License (×lights) + Cloud, depending on flow.
 * Call-for-pricing flows redirect to a configurable quote-request URL instead of cart.
 */
if (!customElements.get('slc-configurator')) {
  customElements.define(
    'slc-configurator',
    class SlcConfigurator extends HTMLElement {
      constructor() {
        super();

        const dataEl = document.getElementById('slc-config-data-' + this.dataset.sectionId);
        if (!dataEl) return;
        this.configData = JSON.parse(dataEl.textContent);

        this.state = {
          commander: null,        // 'new' | 'new-facility' | 'returning'
          returningType: null,    // 'licenses' | 'penguins'
          penguinQty: 0,
          accessMode: null,       // 'offline' | 'lan' | 'cloud'
          licenseTier: null,      // 'tactical' | 'swarm' | 'fleet' | 'galactic'
          lightCount: 0,
          billingPeriod: 'annual',
        };

        this.tierRanges = [
          { min: 1, max: 49, tier: 'tactical' },
          { min: 50, max: 99, tier: 'swarm' },
          { min: 100, max: 149, tier: 'fleet' },
          { min: 150, max: Infinity, tier: 'galactic' },
        ];

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.els = {
          // Steps
          stepCommander:    this.querySelector('#slc-step-commander'),
          stepReturning:    this.querySelector('#slc-step-returning'),
          stepPenguins:     this.querySelector('#slc-step-penguins'),
          stepAccess:       this.querySelector('#slc-step-access'),
          stepLicenseTier:  this.querySelector('#slc-step-license-tier'),
          stepLicenseCall:  this.querySelector('#slc-step-license-call'),
          // Step badges
          stepNumCommander: this.querySelector('[data-step-num="commander"]'),
          stepNumReturning: this.querySelector('[data-step-num="returning"]'),
          stepNumPenguins:  this.querySelector('[data-step-num="penguins"]'),
          stepNumAccess:    this.querySelector('[data-step-num="access"]'),
          stepNumLicTier:   this.querySelector('[data-step-num="license-tier"]'),
          stepNumLicCall:   this.querySelector('[data-step-num="license-call"]'),
          // Penguin qty
          penguinInput:     this.querySelector('[data-penguin-qty]'),
          penguinTotal:     this.querySelector('[data-penguin-total]'),
          // Light qty / tiers
          lightInput:       this.querySelector('[data-light-qty]'),
          tierCards:        this.querySelectorAll('[data-tier-segment]'),
          overLimit:        this.querySelector('[data-over-limit]'),
          // Quote panel
          quoteItems:       this.querySelector('[data-quote-items]'),
          quoteStatus:      this.querySelector('[data-quote-status]'),
          quoteDivider:     this.querySelector('[data-quote-divider]'),
          quoteTotalRow:    this.querySelector('[data-quote-total-row]'),
          totalLabel:       this.querySelector('[data-total-label]'),
          totalValue:       this.querySelector('[data-total-value]'),
          totalNote:        this.querySelector('[data-total-note]'),
          billingToggle:    this.querySelector('[data-billing-toggle]'),
          billingInfo:      this.querySelector('[data-billing-info]'),
          btnCart:          this.querySelector('[data-add-to-cart]'),
          btnQuote:         this.querySelector('[data-add-to-quote]'),
          // Pre-fill helpers
          fromPriceNew:          this.querySelector('[data-from-price-new]'),
          fromPriceNewFacility:  this.querySelector('[data-from-price-new-facility]'),
          penguinUnitPrice:      this.querySelector('[data-penguin-unit-price]'),
        };
      }

      connectedCallback() {
        this.bindEvents();
        this.populateStaticPrices();
        this.updateTierCardPrices();
        this.updateQuote();
        this.setupToastPin();
      }

      bindEvents() {
        // Step 1: commander ownership
        this.querySelectorAll('[data-commander]').forEach((card) => {
          card.addEventListener('click', () => this.selectCommander(card.dataset.commander));
        });

        // Step 2 (returning): licenses or penguins
        this.querySelectorAll('[data-returning]').forEach((card) => {
          card.addEventListener('click', () => this.selectReturning(card.dataset.returning));
        });

        // Penguin qty
        this.els.penguinInput?.addEventListener('input', () => this.onPenguinQtyChange());
        this.els.penguinInput?.addEventListener('change', () => this.onPenguinQtyChange());
        this.querySelector('[data-penguin-increment]')?.addEventListener('click', () => {
          this.els.penguinInput.value = (parseInt(this.els.penguinInput.value, 10) || 0) + 1;
          this.onPenguinQtyChange();
        });
        this.querySelector('[data-penguin-decrement]')?.addEventListener('click', () => {
          this.els.penguinInput.value = Math.max(1, (parseInt(this.els.penguinInput.value, 10) || 0) - 1);
          this.onPenguinQtyChange();
        });

        // Access mode
        this.querySelectorAll('[data-access]').forEach((card) => {
          card.addEventListener('click', () => this.selectAccess(card.dataset.access));
        });

        // Light qty
        this.els.lightInput?.addEventListener('input', () => this.onLightCountChange());
        this.els.lightInput?.addEventListener('change', () => this.onLightCountChange());
        this.querySelector('[data-light-decrement]')?.addEventListener('click', () => {
          const cur = parseInt(this.els.lightInput.value, 10) || 0;
          this.els.lightInput.value = Math.max(0, cur - 1);
          this.onLightCountChange();
        });
        this.querySelector('[data-light-increment]')?.addEventListener('click', () => {
          this.els.lightInput.value = (parseInt(this.els.lightInput.value, 10) || 0) + 1;
          this.onLightCountChange();
        });

        // Billing toggle
        this.querySelectorAll('[data-billing-period]').forEach((btn) => {
          btn.addEventListener('click', () => this.selectBilling(btn.dataset.billingPeriod));
        });

        // Cart / quote actions
        this.els.btnCart?.addEventListener('click', () => this.onPrimaryAction());
        this.els.btnQuote?.addEventListener('click', () => this.addToQuote());
        this.querySelectorAll('[data-request-quote]').forEach((link) => {
          // Anchor handles navigation natively when href is set; only intercept when href is "#"
          link.addEventListener('click', (e) => {
            if (link.getAttribute('href') === '#' || !link.getAttribute('href')) {
              e.preventDefault();
              this.goToQuoteRequest();
            }
          });
        });

        // Clear
        this.querySelector('[data-clear-all]')?.addEventListener('click', () => this.clearAll());
      }

      /* ═══ STATIC PRICE PREFILL ═══ */
      populateStaticPrices() {
        const cmd = this.configData.commander?.price;
        const peng = this.configData.penguin?.price;

        if (this.els.fromPriceNew && cmd != null && peng != null) {
          this.els.fromPriceNew.textContent = 'From ' + this.formatMoney(cmd + peng);
        }
        if (this.els.fromPriceNewFacility && cmd != null && peng != null) {
          this.els.fromPriceNewFacility.textContent = 'From ' + this.formatMoney(cmd + peng);
        }
        if (this.els.penguinUnitPrice && peng != null) {
          this.els.penguinUnitPrice.textContent = this.formatMoney(peng);
        }
      }

      /* ═══ FLOW DETECTION ═══ */
      getFlow() {
        const { commander, returningType } = this.state;
        if (commander === 'new') return 'new';
        if (commander === 'new-facility') return 'new-facility';
        if (commander === 'returning' && returningType === 'licenses') return 'returning-licenses';
        if (commander === 'returning' && returningType === 'penguins') return 'returning-penguins';
        return null;
      }

      /* ═══ STEP 1: COMMANDER ═══ */
      selectCommander(value) {
        this.state.commander = value;

        this.querySelectorAll('[data-commander]').forEach((c) => {
          c.classList.toggle('slc-option-card--selected', c.dataset.commander === value);
        });

        // Reset returning sub-selection if switching away
        if (value !== 'returning') {
          this.state.returningType = null;
          this.querySelectorAll('[data-returning]').forEach((c) => c.classList.remove('slc-option-card--selected'));
          this.els.stepNumReturning?.classList.remove('slc-step__num--done');
        }

        // Penguin defaults
        if (value === 'new' || value === 'new-facility') {
          this.state.penguinQty = 1;
          if (this.els.penguinInput) this.els.penguinInput.value = 1;
          this.updatePenguinTotal();
          this.els.stepNumPenguins?.classList.add('slc-step__num--done');
        } else {
          this.state.penguinQty = 0;
          if (this.els.penguinInput) this.els.penguinInput.value = 1;
          this.updatePenguinTotal();
          this.els.stepNumPenguins?.classList.remove('slc-step__num--done');
        }

        this.els.stepNumCommander?.classList.add('slc-step__num--done');
        this.refreshSteps();

        const next = value === 'returning' ? this.els.stepReturning : this.els.stepPenguins;
        if (next) next.scrollIntoView({ behavior: 'smooth', block: 'center' });

        this.updateQuote();
      }

      /* ═══ STEP 2 (returning) ═══ */
      selectReturning(value) {
        this.state.returningType = value;

        this.querySelectorAll('[data-returning]').forEach((c) => {
          c.classList.toggle('slc-option-card--selected', c.dataset.returning === value);
        });
        this.els.stepNumReturning?.classList.add('slc-step__num--done');

        if (value === 'penguins') {
          this.state.penguinQty = 1;
          if (this.els.penguinInput) this.els.penguinInput.value = 1;
          this.updatePenguinTotal();
          this.els.stepNumPenguins?.classList.add('slc-step__num--done');
        } else {
          this.state.penguinQty = 0;
        }

        this.refreshSteps();

        const next = value === 'licenses' ? this.els.stepLicenseCall : this.els.stepPenguins;
        if (next) next.scrollIntoView({ behavior: 'smooth', block: 'center' });

        this.updateQuote();
      }

      /* ═══ PENGUIN QTY ═══ */
      onPenguinQtyChange() {
        const raw = parseInt(this.els.penguinInput.value, 10);
        const qty = isNaN(raw) || raw < 1 ? 1 : raw;
        if (qty !== raw) this.els.penguinInput.value = qty;
        this.state.penguinQty = qty;

        this.updatePenguinTotal();
        this.els.stepNumPenguins?.classList.add('slc-step__num--done');
        this.updateQuote();
      }

      updatePenguinTotal() {
        if (!this.els.penguinTotal) return;
        const peng = this.configData.penguin?.price;
        if (peng == null) {
          this.els.penguinTotal.innerHTML = '';
          return;
        }
        const qty = this.state.penguinQty || 1;
        this.els.penguinTotal.innerHTML = '<strong>' + this.formatMoney(qty * peng) + '</strong>';
      }

      /* ═══ ACCESS MODE ═══ */
      selectAccess(mode) {
        this.state.accessMode = mode;

        this.querySelectorAll('[data-access]').forEach((c) => {
          c.classList.toggle('slc-option-card--selected', c.dataset.access === mode);
        });
        this.els.stepNumAccess?.classList.add('slc-step__num--done');

        this.refreshSteps();

        const nextLicense = !this.els.stepLicenseTier?.classList.contains('slc-hidden')
          ? this.els.stepLicenseTier
          : (!this.els.stepLicenseCall?.classList.contains('slc-hidden') ? this.els.stepLicenseCall : null);
        if (nextLicense) nextLicense.scrollIntoView({ behavior: 'smooth', block: 'center' });

        this.updateQuote();
      }

      /* ═══ LIGHT COUNT → TIER ═══ */
      getTierForCount(count) {
        for (const range of this.tierRanges) {
          if (count >= range.min && count <= range.max) return range.tier;
        }
        return null;
      }

      onLightCountChange() {
        const raw = parseInt(this.els.lightInput.value, 10);
        const count = isNaN(raw) || raw < 1 ? 0 : raw;
        this.state.lightCount = count;

        const tier = this.getTierForCount(count);
        this.state.licenseTier = tier;
        const overLimit = tier === 'galactic';

        this.els.tierCards.forEach((c) => c.classList.toggle('slc-tier-strip__cell--active', c.dataset.tierSegment === tier));
        this.els.overLimit?.classList.toggle('slc-hidden', !overLimit);

        if (tier && !overLimit) {
          this.els.stepNumLicTier?.classList.add('slc-step__num--done');
        } else {
          this.els.stepNumLicTier?.classList.remove('slc-step__num--done');
        }

        this.updateQuote();
      }

      /* ═══ BILLING ═══ */
      selectBilling(period) {
        this.state.billingPeriod = period;
        this.querySelectorAll('[data-billing-period]').forEach((b) => {
          b.classList.toggle('slc-billing-segment--selected', b.dataset.billingPeriod === period);
        });
        this.updateTierCardPrices();
        this.updateQuote();
      }

      updateTierCardPrices() {
        const isMonthly = this.state.billingPeriod === 'monthly';
        const period = isMonthly ? '/mo' : '/yr';
        ['tactical', 'swarm', 'fleet'].forEach((tier) => {
          const el = this.querySelector('[data-tier-segment-price="' + tier + '"]');
          if (!el) return;
          const lic = this.configData.licenses[tier];
          if (!lic) return;
          const priceData = isMonthly ? lic.monthly : lic.annual;
          const price = priceData?.price != null ? this.formatMoney(priceData.price) : '—';
          el.innerHTML = price + '<span class="slc-period">' + period + '</span> per light';
        });
      }

      /* ═══ STEP VISIBILITY + DYNAMIC RENUMBERING ═══ */
      refreshSteps() {
        const flow = this.getFlow();
        const hasLanOrCloud = this.state.accessMode === 'lan' || this.state.accessMode === 'cloud';

        const showReturning    = this.state.commander === 'returning';
        const showPenguins     = flow === 'new' || flow === 'new-facility' || flow === 'returning-penguins';
        const showAccess       = flow === 'new' || flow === 'new-facility';
        const showLicenseTier  = flow === 'new' && hasLanOrCloud;
        const showLicenseCall  = (flow === 'new-facility' && hasLanOrCloud) || flow === 'returning-licenses';

        this.els.stepReturning?.classList.toggle('slc-hidden', !showReturning);
        this.els.stepPenguins?.classList.toggle('slc-hidden', !showPenguins);
        this.els.stepAccess?.classList.toggle('slc-hidden', !showAccess);
        this.els.stepLicenseTier?.classList.toggle('slc-hidden', !showLicenseTier);
        this.els.stepLicenseCall?.classList.toggle('slc-hidden', !showLicenseCall);

        // Clear stale state when steps disappear
        if (!showAccess && this.state.accessMode !== null) {
          this.state.accessMode = null;
          this.querySelectorAll('[data-access]').forEach((c) => c.classList.remove('slc-option-card--selected'));
          this.els.stepNumAccess?.classList.remove('slc-step__num--done');
        }
        if (!showLicenseTier && this.state.lightCount > 0) {
          this.state.lightCount = 0;
          this.state.licenseTier = null;
          if (this.els.lightInput) this.els.lightInput.value = '';
          this.els.tierCards.forEach((c) => c.classList.remove('slc-tier-strip__cell--active'));
          this.els.overLimit?.classList.add('slc-hidden');
          this.els.stepNumLicTier?.classList.remove('slc-step__num--done');
        }

        // Renumber visible step badges (Step 1 is always Commander)
        let num = 1;
        if (this.els.stepNumCommander) this.els.stepNumCommander.textContent = num;
        if (showReturning && this.els.stepNumReturning)    this.els.stepNumReturning.textContent = ++num;
        if (showPenguins && this.els.stepNumPenguins)      this.els.stepNumPenguins.textContent  = ++num;
        if (showAccess && this.els.stepNumAccess)          this.els.stepNumAccess.textContent    = ++num;
        if (showLicenseTier && this.els.stepNumLicTier)    this.els.stepNumLicTier.textContent   = ++num;
        if (showLicenseCall && this.els.stepNumLicCall)    this.els.stepNumLicCall.textContent   = ++num;
      }

      /* ═══ QUOTE PANEL ═══ */
      updateQuote() {
        const flow = this.getFlow();
        const { penguinQty, accessMode, licenseTier, lightCount, billingPeriod } = this.state;
        const isMonthly = billingPeriod === 'monthly';
        const overLimit = licenseTier === 'galactic';
        const hasLanOrCloud = accessMode === 'lan' || accessMode === 'cloud';

        const wantsCommander    = flow === 'new' || flow === 'new-facility';
        const wantsPenguins     = flow === 'new' || flow === 'new-facility' || flow === 'returning-penguins';
        const wantsLicenseTier  = flow === 'new' && hasLanOrCloud;
        const wantsLicenseCall  = (flow === 'new-facility' && hasLanOrCloud) || flow === 'returning-licenses';
        const wantsCloud        = (flow === 'new' || flow === 'new-facility') && accessMode === 'cloud';

        const items = [];
        let total = 0;
        let hasRecurring = false;
        let hasCallForPricing = false;

        if (wantsCommander && this.configData.commander?.price != null) {
          items.push({
            name: this.configData.commander.title,
            detail: (this.configData.commander.sku || '') + (this.configData.commander.sku ? ' — ' : '') + 'one-time',
            price: this.configData.commander.price,
            recurring: false,
          });
          total += this.configData.commander.price;
        }

        if (wantsPenguins && penguinQty > 0 && this.configData.penguin?.price != null) {
          const cost = penguinQty * this.configData.penguin.price;
          items.push({
            name: this.configData.penguin.title + (penguinQty > 1 ? ' × ' + penguinQty : ''),
            detail: (this.configData.penguin.sku || '') + (this.configData.penguin.sku ? ' — ' : '') + 'hardware, one-time',
            price: cost,
            recurring: false,
          });
          total += cost;
        }

        if (wantsLicenseTier && licenseTier && lightCount > 0 && !overLimit) {
          const lic = this.configData.licenses[licenseTier];
          const licData = isMonthly ? lic.monthly : lic.annual;
          const perLight = licData?.price;
          const period = isMonthly ? '/mo' : '/yr';
          if (perLight != null) {
            items.push({
              name: lic.name,
              detail: lightCount + ' light' + (lightCount === 1 ? '' : 's') + ' × ' + this.formatMoney(perLight) + period + ' each',
              price: perLight * lightCount,
              period,
              recurring: true,
            });
            total += perLight * lightCount;
            hasRecurring = true;
          }
        }

        if (wantsLicenseCall) {
          items.push({
            name: 'Light Licenses',
            detail: 'Volume pricing based on previous orders',
            callForPricing: true,
          });
          hasCallForPricing = true;
        }

        if (wantsCloud) {
          const cloudData = isMonthly ? this.configData.cloud.monthly : this.configData.cloud.annual;
          if (cloudData?.price != null) {
            items.push({
              name: this.configData.cloud.name,
              detail: 'Remote access anywhere',
              price: cloudData.price,
              period: isMonthly ? '/mo' : '/yr',
              recurring: true,
            });
            total += cloudData.price;
            hasRecurring = true;
          }
        }

        this.renderQuote(items, total, { isMonthly, hasRecurring, hasCallForPricing, overLimit, flow, hasLanOrCloud });
      }

      renderQuote(items, total, ctx) {
        const { isMonthly, hasRecurring, hasCallForPricing, overLimit, flow, hasLanOrCloud } = ctx;
        const { penguinQty, accessMode, licenseTier, lightCount } = this.state;

        // Billing toggle visibility — show whenever any priced recurring item is in play
        const wantsLicenseTier = flow === 'new' && hasLanOrCloud;
        const wantsCloud = (flow === 'new' || flow === 'new-facility') && accessMode === 'cloud';
        const showBilling = wantsCloud || wantsLicenseTier;
        this.els.billingToggle?.classList.toggle('slc-hidden', !showBilling);
        this.els.billingInfo?.classList.toggle('slc-hidden', !(isMonthly && hasRecurring));

        // Render items
        this.els.quoteItems.innerHTML = items.map((item) => {
          if (item.callForPricing) {
            return `
              <div class="slc-quote__item">
                <div>
                  <div class="slc-quote__item-name">${this.escapeHtml(item.name)}</div>
                  <div class="slc-quote__item-detail">${this.escapeHtml(item.detail)}</div>
                </div>
                <div class="slc-quote__item-price">
                  <span class="slc-quote__item-price--call">Call for pricing</span>
                </div>
              </div>`;
          }
          const priceMain = this.formatMoney(item.price);
          const periodLabel = item.recurring ? item.period : '';
          return `
            <div class="slc-quote__item">
              <div>
                <div class="slc-quote__item-name">${this.escapeHtml(item.name)}</div>
                <div class="slc-quote__item-detail">${this.escapeHtml(item.detail)}</div>
              </div>
              <div class="slc-quote__item-price">
                ${priceMain}${periodLabel ? '<span class="slc-period">' + periodLabel + '</span>' : ''}
              </div>
            </div>`;
        }).join('');

        // Total / footer
        const hasPricedItems = total > 0;
        if (hasPricedItems && !overLimit) {
          this.els.quoteDivider.style.display = '';
          this.els.quoteTotalRow.style.display = '';
          if (this.els.totalLabel) this.els.totalLabel.textContent = hasCallForPricing ? 'Subtotal' : 'Total';
          if (this.els.totalValue) this.els.totalValue.textContent = this.formatMoney(total);
        } else {
          this.els.quoteDivider.style.display = 'none';
          this.els.quoteTotalRow.style.display = 'none';
        }
        this.els.totalNote?.classList.toggle('slc-hidden', !(hasCallForPricing && hasPricedItems));

        // Status message
        if (!flow) {
          this.els.quoteStatus.classList.remove('slc-hidden');
          this.els.quoteStatus.textContent = this.state.commander === 'returning'
            ? 'Choose what you need above to continue'
            : 'Choose an option above to get started';
        } else if ((flow === 'new' || flow === 'new-facility' || flow === 'returning-penguins') && penguinQty < 1) {
          this.els.quoteStatus.classList.remove('slc-hidden');
          this.els.quoteStatus.textContent = 'Enter the number of Penguins to continue';
        } else if ((flow === 'new' || flow === 'new-facility') && !accessMode) {
          this.els.quoteStatus.classList.remove('slc-hidden');
          this.els.quoteStatus.textContent = 'Choose your access mode to continue';
        } else if (wantsLicenseTier && (!licenseTier || lightCount < 1 || overLimit)) {
          this.els.quoteStatus.classList.remove('slc-hidden');
          this.els.quoteStatus.textContent = overLimit
            ? '150+ lights — please request a custom quote'
            : 'Enter the number of lights to complete your configuration';
        } else {
          this.els.quoteStatus.classList.add('slc-hidden');
        }

        // Cart button state
        let isComplete = false;
        if (flow === 'new') {
          isComplete = penguinQty > 0 && (
            accessMode === 'offline' ||
            (hasLanOrCloud && licenseTier != null && lightCount > 0 && !overLimit)
          );
        } else if (flow === 'new-facility') {
          isComplete = penguinQty > 0 && accessMode != null;
        } else if (flow === 'returning-licenses') {
          isComplete = true;
        } else if (flow === 'returning-penguins') {
          isComplete = penguinQty > 0;
        }

        this.els.btnCart.disabled = !isComplete;
        const btnText = this.els.btnCart.querySelector('.slc-btn-cart__text');
        if (btnText) {
          if (!isComplete) {
            btnText.textContent = 'Complete your configuration above';
          } else if (hasCallForPricing) {
            btnText.textContent = 'Request a Quote';
          } else {
            btnText.textContent = 'Add to Cart';
          }
        }

        // Add-to-quote button: shown only once the config is complete and
        // resolves to concrete, purchasable line items — mirroring the cart
        // button's "Add to Cart" active state. Pure call-for-pricing configs
        // (e.g. returning-licenses) yield none, so it stays hidden there.
        if (this.els.btnQuote) {
          const showQuote = isComplete && this.buildQuoteItems().length > 0;
          this.els.btnQuote.classList.toggle('slc-hidden', !showQuote);
        }
      }

      /* ═══ PRIMARY ACTION ═══ */
      onPrimaryAction() {
        if (this.els.btnCart.disabled) return;
        const flow = this.getFlow();
        const isQuoteFlow = flow === 'new-facility' || flow === 'returning-licenses';

        if (isQuoteFlow) {
          this.goToQuoteRequest();
          return;
        }
        this.addToCart();
      }

      goToQuoteRequest() {
        const url = this.configData.quoteRequestUrl || '/pages/contact-us';
        window.location.href = url;
      }

      /* ═══ ADD TO CART ═══ */
      async addToCart() {
        const btn = this.els.btnCart;
        if (btn.getAttribute('aria-disabled') === 'true') return;

        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('slc-loading');

        const items = this.buildCartItems();
        if (items.length === 0) {
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('slc-loading');
          return;
        }

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ items }),
          });

          const data = await response.json();

          if (data.status) {
            console.error('Cart add error:', data);
            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'slc-configurator',
                errors: data.errors || data.description,
                message: data.message,
              });
            }
            return;
          }

          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'slc-configurator',
              cartData: data,
            });
          }

          if (this.cart) {
            this.cart.setActiveElement(document.activeElement);
            const sectionIds = this.cart.getSectionsToRender().map((s) => s.id);
            const sectionsResponse = await fetch(`${window.routes.cart_url}?sections=${sectionIds.join(',')}`);
            const sectionsData = await sectionsResponse.json();

            const lastItem = Array.isArray(data.items) ? data.items[data.items.length - 1] : data;
            this.cart.renderContents({ key: lastItem.key, id: lastItem.id, items: data.items || data, sections: sectionsData });
          } else {
            window.location = window.routes.cart_url;
          }
        } catch (e) {
          console.error('Cart submission failed:', e);
        } finally {
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('slc-loading');
        }
      }

      // Shopify /cart/add line items — the same configured set as the quote
      // cart, reduced to the {id, quantity} shape the cart endpoint expects.
      buildCartItems() {
        return this.buildQuoteItems().map((item) => ({
          id: item.variant_id,
          quantity: item.quantity,
        }));
      }

      // The configured line items, enriched with title/sku/price for the
      // storefront quote cart. Single source of truth for which variants a
      // completed configuration resolves to (see buildCartItems above).
      buildQuoteItems() {
        const flow = this.getFlow();
        const { penguinQty, accessMode, licenseTier, lightCount, billingPeriod } = this.state;
        const isMonthly = billingPeriod === 'monthly';
        const items = [];

        const wantsCommander = flow === 'new' || flow === 'new-facility';
        const wantsPenguins  = flow === 'new' || flow === 'new-facility' || flow === 'returning-penguins';
        const wantsLicense   = flow === 'new' && (accessMode === 'lan' || accessMode === 'cloud') && licenseTier && licenseTier !== 'galactic' && lightCount > 0;
        const wantsCloud     = (flow === 'new' || flow === 'new-facility') && accessMode === 'cloud';

        const commander = this.configData.commander;
        if (wantsCommander && commander?.variantId) {
          items.push({
            variant_id: String(commander.variantId),
            quantity: 1,
            product_title: commander.title,
            sku: commander.sku,
            price_cents: commander.price,
            image_url: commander.image,
          });
        }

        const penguin = this.configData.penguin;
        if (wantsPenguins && penguinQty > 0 && penguin?.variantId) {
          items.push({
            variant_id: String(penguin.variantId),
            quantity: penguinQty,
            product_title: penguin.title,
            sku: penguin.sku,
            price_cents: penguin.price,
            image_url: penguin.image,
          });
        }

        if (wantsLicense) {
          const lic = this.configData.licenses[licenseTier];
          const tier = isMonthly ? lic.monthly : lic.annual;
          if (tier?.variantId) {
            items.push({
              variant_id: String(tier.variantId),
              quantity: lightCount,
              product_title: lic.name,
              variant_title: isMonthly ? 'Monthly' : 'Annual',
              price_cents: tier.price,
              image_url: this.configData.licenseImage,
            });
          }
        }

        if (wantsCloud) {
          const cloud = this.configData.cloud;
          const tier = isMonthly ? cloud.monthly : cloud.annual;
          if (tier?.variantId) {
            items.push({
              variant_id: String(tier.variantId),
              quantity: 1,
              product_title: cloud.name,
              variant_title: isMonthly ? 'Monthly' : 'Annual',
              price_cents: tier.price,
              image_url: cloud.image,
            });
          }
        }

        return items;
      }

      /* ═══ ADD TO QUOTE ═══ */
      addToQuote() {
        const btn = this.els.btnQuote;
        if (!btn || btn.classList.contains('slc-hidden')) return;
        if (!window.QuoteCart) return;

        const items = this.buildQuoteItems();
        if (items.length === 0) return;

        // QuoteCart.add returns null when the cart is full (or the item fails
        // validation). Track the last success so the toast has something to
        // show; if nothing landed, surface the cap instead.
        let lastAdded = null;
        items.forEach((item) => {
          const result = window.QuoteCart.add(item);
          if (result) lastAdded = result;
        });

        if (!lastAdded) {
          this.flashQuoteLabel(`Quote is full (${window.QuoteCart.MAX_ITEMS} items max)`);
          return;
        }

        this.flashQuoteLabel('Added to quote!');
        window.dispatchEvent(
          new CustomEvent('quote-cart:added', { detail: { item: lastAdded } })
        );
      }

      flashQuoteLabel(message) {
        const btn = this.els.btnQuote;
        if (!btn) return;
        if (this.quoteLabelDefault == null) {
          this.quoteLabelDefault = btn.textContent.trim();
        }
        btn.textContent = message;
        clearTimeout(this.quoteFlashTimer);
        this.quoteFlashTimer = setTimeout(() => {
          btn.textContent = this.quoteLabelDefault;
        }, 1600);
      }

      // The shared quote toast anchors just under the site header. On this
      // long page the quote button sits far below the fold, so while the
      // toast is open we pin it to the top of the viewport — but let it slot
      // back under the header once the user scrolls up to it. Scoped to this
      // template: this script and slc-configurator.css only load here.
      setupToastPin() {
        const toast = document.querySelector('#quote-notification');
        if (!toast) return;
        const header = document.querySelector('.header-wrapper') || document.querySelector('header');

        const updateTop = () => {
          const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
          toast.style.top = `${Math.max(0, headerBottom)}px`;
        };

        // Observe the `class` attribute (active/inactive) but only ever mutate
        // inline styles in response — never the class — so this callback can't
        // retrigger itself into an infinite loop. A flag guards against acting
        // on unrelated class flips (animate/focused).
        let pinned = false;
        const observer = new MutationObserver(() => {
          const active = toast.classList.contains('active');
          if (active === pinned) return;
          pinned = active;

          if (active) {
            toast.style.position = 'fixed';
            toast.style.zIndex = '4';
            updateTop();
            window.addEventListener('scroll', updateTop, { passive: true });
            window.addEventListener('resize', updateTop, { passive: true });
          } else {
            toast.style.position = '';
            toast.style.zIndex = '';
            toast.style.top = '';
            window.removeEventListener('scroll', updateTop);
            window.removeEventListener('resize', updateTop);
          }
        });
        observer.observe(toast, { attributes: true, attributeFilter: ['class'] });
      }

      /* ═══ CLEAR ═══ */
      clearAll() {
        this.state = {
          commander: null,
          returningType: null,
          penguinQty: 0,
          accessMode: null,
          licenseTier: null,
          lightCount: 0,
          billingPeriod: 'annual',
        };

        this.querySelectorAll('.slc-option-card').forEach((c) => c.classList.remove('slc-option-card--selected'));
        [
          this.els.stepNumCommander,
          this.els.stepNumReturning,
          this.els.stepNumPenguins,
          this.els.stepNumAccess,
          this.els.stepNumLicTier,
          this.els.stepNumLicCall,
        ].forEach((n) => n?.classList.remove('slc-step__num--done'));

        this.els.stepReturning?.classList.add('slc-hidden');
        this.els.stepPenguins?.classList.add('slc-hidden');
        this.els.stepAccess?.classList.add('slc-hidden');
        this.els.stepLicenseTier?.classList.add('slc-hidden');
        this.els.stepLicenseCall?.classList.add('slc-hidden');

        if (this.els.penguinInput) this.els.penguinInput.value = 1;
        this.updatePenguinTotal();
        if (this.els.lightInput) this.els.lightInput.value = '';
        this.els.tierCards.forEach((c) => c.classList.remove('slc-tier-strip__cell--active'));
        this.els.overLimit?.classList.add('slc-hidden');

        this.querySelectorAll('[data-billing-period]').forEach((btn) => {
          btn.classList.toggle('slc-billing-segment--selected', btn.dataset.billingPeriod === 'annual');
        });

        this.updateTierCardPrices();
        this.updateQuote();
      }

      /* ═══ HELPERS ═══ */
      formatMoney(cents) {
        if (cents === null || cents === undefined) return 'Custom';
        const dollars = cents / 100;
        return '$' + dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }

      escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
      }
    }
  );
}
